import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useNavigate} from 'react-router';

import {FORGOT_PASSWORD_EVENTS} from '@/app/auth';
import {useResetPasswordTrackers} from '@/app/main/authentication/pages/ResetPassword/trackers';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {useAmplifyAuth} from '@/hooks';
import {ForgotPasswordInput, GetTokenInput} from '@/services';
import logError from '@/utils/errors/logError';
import {generateUuid} from '@/utils/randomGenerator';

import {defaultToast, errorMap} from './constants';
import {useResetPasswordUsersMutation} from './ResetPasswordApi';
import {ViewModelContextType} from './types';

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [SERVICES] ----]
  const [sharedKey, setSharedKey] = useState<string>('');
  //#endregion

  //#region [---- [SERVICES] ----]
  const navigate = useNavigate();
  const [handleGetPasswordToken] = useResetPasswordUsersMutation();
  //#endregion

  //#region [---- [APIS] ----]
  const {sendRecoveryEmail} = useAmplifyAuth();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [recoveryToken, setRecoveryToken] = useState<string>('');
  const {trackResetPassword} = useResetPasswordTrackers();

  useEffect(() => {
    trackResetPassword(FORGOT_PASSWORD_EVENTS.VIEW_FORGOT_PASSWORD_SCREEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handlePressSendRecoveryEmail = useCallback(async () => {
    try {
      trackResetPassword(
        FORGOT_PASSWORD_EVENTS.SELECT_FORGOT_PASSWORD_SEND_ME_THE_RECOVERY_LINK,
      );
      const key = generateUuid();
      setSharedKey(key);
      const request: ForgotPasswordInput = {
        username: email,
      };
      const metadata = {
        shared_key: key,
        target: email,
      };

      await sendRecoveryEmail(request, metadata);
      setShowConfirmation(true);
      trackResetPassword(
        FORGOT_PASSWORD_EVENTS.VIEW_FORGOT_PASSWORD_RECOVERY_EMAIL_SENT_SCREEN,
      );
    } catch (error) {
      trackResetPassword(
        FORGOT_PASSWORD_EVENTS.API_FORGOT_PASSWORD_SEND_ME_THE_RECOVERY_LINK_ERROR,
        error,
      );
      logError(error, {
        fileName: 'ResetPassword ViewModelContext',
        functionName: 'handlePressSendRecoveryEmail',
        extraTags: {
          'user.email': email,
        },
      });
      const errName = error?.name || '';
      const {TITLE: summary, DETAILS: detail} =
        errorMap[errName] || defaultToast;
      showSnackbar({
        summary: summary,
        message: detail,
      });
    }
  }, [email, sendRecoveryEmail, trackResetPassword]);

  const handleGetRecoveryToken = useCallback(async () => {
    try {
      const payload: Partial<GetTokenInput> = {
        target: email,
        shared_key: sharedKey,
      };

      const result = await handleGetPasswordToken(payload);

      setRecoveryToken(result?.data?.code ?? '');
    } catch (error) {
      logError(error, {
        fileName: 'ResetPassword ViewModelContext',
        functionName: 'handleGetRecoveryToken',
        extraTags: {
          'user.email': email,
        },
      });
      const errorMessage = error?.message ?? 'Get recovery token problem';
      showSnackbar({
        message: errorMessage,
      });
    }
  }, [email, sharedKey, handleGetPasswordToken]);

  const goToRecoveryLink = useCallback(() => {
    navigate(`/authentication/password/reset?code=${recoveryToken}`);
  }, [recoveryToken, navigate]);

  const disabledButtonSendRecoveryEmail = useMemo(() => {
    return !email || emailError;
  }, [email, emailError]);

  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        email,
        showConfirmation,
        recoveryToken,
        handlePressSendRecoveryEmail,
        handleGetRecoveryToken,
        goToRecoveryLink,
        setEmail,
        navigate,
        disabledButtonSendRecoveryEmail,
        setEmailError,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('ResetPassword ViewModelContext is undefined');
  }
  return context;
}
